import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, BarChart3, Globe2, Layers } from 'lucide-react';
import './AboutPage.css';

const pillars = [
  { icon: Globe2, title: 'A broader perspective', text: 'Explore digital assets, futures and CFDs in one place. Follow different markets and build a clearer picture of the opportunities available to you.' },
  { icon: BarChart3, title: 'Clarity in every decision', text: 'Bring prices, charts, positions and portfolio information into a single workspace, so you can focus on understanding the market and your exposure.' },
  { icon: Layers, title: 'Tools that work together', text: 'Move between trading, your wallet and portfolio tools with a consistent experience designed to make complex information easier to navigate.' },
];

export default function AboutPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'About Valore | Who we are';
    window.scrollTo(0, 0);
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="about-page">
      <a className="about-skip" href="#about-main">Skip to content</a>
      <header className="about-header">
        <Link to="/" aria-label="Valore home"><img src="/logo.svg" alt="Valore" width="185" height="30" /></Link>
        <nav aria-label="Main navigation" className="about-header-nav">
          <Link to="/about" aria-current="page">About us</Link>
          <Link to="/trading-fees" className="about-fees-link">Trading fees</Link>
          <Link to="/signin">Sign in <ArrowUpRight size={15} aria-hidden="true" /></Link>
        </nav>
      </header>

      <main id="about-main">
        <section className="about-hero" aria-labelledby="about-title">
          <div className="about-wrap about-hero-grid">
            <div>
              <p className="about-eyebrow">Who we are / About Valore</p>
              <h1 id="about-title">A clearer view.<br />A world of <span>possibility.</span></h1>
              <p className="about-hero-copy">Markets move. Your ambitions matter. We bring the tools and perspective together to help you take your next step with greater understanding.</p>
              <a href="#our-purpose" className="about-text-link">Discover our purpose <ArrowRight size={19} aria-hidden="true" /></a>
            </div>
            <div className="about-visual" aria-hidden="true">
              <div className="about-orbit about-orbit-one" /><div className="about-orbit about-orbit-two" />
              <div className="about-visual-grid" />
              <div className="about-monogram">V<span>.</span></div>
              <div className="about-visual-caption">PERSPECTIVE INTO POSSIBILITY</div>
            </div>
          </div>
        </section>

        <nav className="about-section-nav" aria-label="On this page">
          <div className="about-wrap"><a href="#our-purpose">Our purpose</a><a href="#our-platform">Our platform</a><a href="#our-principles">Our principles</a><a href="#locations">Locations</a></div>
        </nav>

        <section id="our-purpose" className="about-wrap about-section" aria-labelledby="purpose-title">
          <div className="about-section-intro">
            <p className="about-eyebrow">Our purpose</p>
            <div><h2 id="purpose-title">Make markets easier<br />to understand and explore.</h2><p>Valore is a trading platform that brings market information, trading tools and portfolio management into one connected experience. Our aim is to help people approach financial markets with more context and a better understanding of their choices.</p></div>
          </div>
          <div className="about-pillars">
            {pillars.map(({ icon: Icon, title, text }, index) => (
              <article key={title} className="about-pillar">
                <div className="about-pillar-top"><Icon size={32} strokeWidth={1.4} aria-hidden="true" /><span>0{index + 1}</span></div>
                <h3>{title}</h3><p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="our-platform" className="about-platform" aria-labelledby="platform-title">
          <div className="about-wrap about-platform-grid">
            <div><p className="about-eyebrow">One connected platform</p><h2 id="platform-title">Your markets.<br />Your perspective.</h2><p>From exploring a new instrument to reviewing an open position, Valore brings the details that matter into view.</p><Link to="/signup" className="about-button">Explore Valore <ArrowRight size={18} aria-hidden="true" /></Link></div>
            <div className="about-capabilities">
              {[
                ['Markets & trading', 'Access spot trading, futures and CFDs with charts and order tools to support your research.'],
                ['Wallet & portfolio', 'Review balances, transactions and positions to understand how your portfolio fits together.'],
                ['Information & insight', 'Follow market prices and news alongside the instruments you are exploring.'],
              ].map(([title, text], index) => <article key={title}><span className="about-capability-number">0{index + 1}</span><div><h3>{title}</h3><p>{text}</p></div></article>)}
            </div>
          </div>
        </section>

        <section id="our-principles" className="about-wrap about-section" aria-labelledby="principles-title">
          <p className="about-eyebrow">What guides us</p><h2 id="principles-title">Built around better understanding.</h2>
          <div className="about-principles">
            <article><h3>Put the experience first.</h3><p>We aim to make everyday tasks straightforward, with a consistent interface and information that is easy to find.</p></article>
            <article><h3>Make the details visible.</h3><p>Prices, fees and exposure belong in the conversation. Understanding the details is part of making an informed decision.</p><Link to="/trading-fees" className="about-text-link">View trading fees <ArrowRight size={17} aria-hidden="true" /></Link></article>
            <article><h3>Keep improving.</h3><p>Markets and people's needs evolve. Our approach is to keep refining the tools and the experience around them.</p></article>
          </div>
        </section>

        <section id="locations" className="about-locations" aria-labelledby="locations-title">
          <div className="about-wrap about-section">
            <p className="about-eyebrow">Locations</p>
            <h2 id="locations-title">New York City &amp; London.</h2>
            <div className="about-location-grid">
              <article className="about-location-card">
                <p className="about-eyebrow">United States</p>
                <h3>New York City</h3>
                <address>10 E 53rd St<br />New York, NY 10022<br />United States</address>
                <a className="about-text-link" href="https://www.google.com/maps/search/?api=1&query=10+E+53rd+St+New+York+NY+10022" target="_blank" rel="noopener noreferrer">View New York office on map <ArrowUpRight size={17} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span></a>
              </article>
              <article className="about-location-card">
                <p className="about-eyebrow">United Kingdom</p>
                <h3>London</h3>
                <address>Flat 30, Park Towers<br />2 Brick Street, Mayfair<br />London, W1J 7DD<br />United Kingdom</address>
                <p className="about-location-context">In Mayfair, Central London, just off Park Lane and near Hyde Park, with Green Park Underground station a short walk away.</p>
                <a className="about-text-link" href="https://www.google.com/maps/search/?api=1&query=Park+Towers+2+Brick+Street+Mayfair+London+W1J+7DD+United+Kingdom" target="_blank" rel="noopener noreferrer">View London location on map <ArrowUpRight size={17} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span></a>
              </article>
            </div>
          </div>
        </section>

        <section className="about-next" aria-labelledby="next-title"><div className="about-wrap"><div><p className="about-eyebrow">Keep exploring</p><h2 id="next-title">Get to know Valore.</h2></div><Link to="/signup" className="about-button">Create an account <ArrowUpRight size={20} aria-hidden="true" /></Link></div></section>
      </main>

      <footer className="about-footer about-wrap">
        <div className="about-footer-top"><Link to="/" className="about-wordmark">VALORE<span>.</span></Link><nav aria-label="Footer navigation"><Link to="/about">About us</Link><Link to="/trading-fees">Trading fees</Link><Link to="/signin">Sign in</Link></nav></div>
        <p>Trading involves risk. Asset prices can fall as well as rise, and leveraged products can magnify losses. Consider your objectives and risk tolerance before trading.</p>
        <small>© {new Date().getFullYear()} Valore. All rights reserved.</small>
      </footer>
    </div>
  );
}
